import { Column, Entity, JoinColumn, JoinTable, ManyToMany, ManyToOne, PrimaryGeneratedColumn } from 'typeorm';
import { Business, PublicBusiness } from '../business/business.entity';
import { Site, PublicSite } from '../site/site.entity';
import { Asset, PublicAsset } from '../asset/asset.entity';

export type PublicAssetCollection = Pick<
	AssetCollection,
	'id' | 'name' | 'siteId' | 'created' | 'modified'
> & {
	site?: PublicSite;
	businesses?: Array<PublicBusiness>;
	assets?: Array<PublicAsset>;
	assetCount?: number;
};

@Entity('assetCollections')
export class AssetCollection {
	constructor(value?: Partial<AssetCollection>) {
		if (!value) {
			return;
		}
		for (const key of Object.keys(value)) {
			this[key] = value[key];
		}
	}

	@PrimaryGeneratedColumn('uuid')
	id: string;

	@Column('text', { nullable: false })
	name: string;

	@Column('uuid', { nullable: false })
	siteId: string;

	@ManyToOne(() => Site, {
		nullable: false,
		onDelete: 'CASCADE'
	})
	@JoinColumn({ name: 'siteId' })
	site?: Site | Partial<Site>;

	@ManyToMany(() => Business, {
		nullable: true
	})
	@JoinTable({
		name: 'assetCollectionBusinesses',
		joinColumn: {
			name: 'assetCollectionsId',
			referencedColumnName: 'id'
		},
		inverseJoinColumn: {
			name: 'businessesId',
			referencedColumnName: 'id'
		}
	})
	businesses?: Business[] | Array<Partial<Business>>;

	@ManyToMany(() => Asset, asset => asset.collections)
	assets?: Asset[];

	assetCount?: number;

	@Column('timestamptz', { default: () => 'NOW()' })
	created: string;

	@Column('timestamptz', { default: () => 'NOW()' })
	modified: string;

	public toPublic(): PublicAssetCollection {
		const pub: PublicAssetCollection = {
			id: this.id,
			name: this.name,
			siteId: this.siteId,
			created: this.created,
			modified: this.modified
		};

		if (this.site) {
			pub.site = new Site(this.site).toPublic();
		}

		if (this.businesses?.length) {
			pub.businesses = this.businesses.map(business => new Business(business).toPublic());
		}

		if (typeof this.assetCount === 'number') {
			pub.assetCount = this.assetCount;
		}

		if (this.assets?.length) {
			pub.assets = this.assets.map(asset => new Asset(asset).toPublic(['collections']));
			pub.assetCount = this.assets.length;
		}

		return pub;
	}
}
